import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

/**
 * Single-textarea modal used by every "needs a reason" Care Pathways flow
 * (PTV2-1174): Skip step (required rationale), Mark step complete (optional
 * note), Cancel pathway (required reason), Complete pathway manually
 * (required reason). The required-ness is parameterized via `requireText`;
 * when true, the Confirm button stays disabled until the user types something.
 *
 * Mounts inside the `CarePathwaysSection` and lifts its state up there — the
 * dialog itself is presentational so the parent owns the busy / error state.
 */

interface ReasonDialogProps {
  readonly title: string;
  readonly description: string;
  readonly fieldLabel: string;
  readonly fieldPlaceholder?: string;
  readonly confirmLabel: string;
  readonly requireText: boolean;
  readonly busy?: boolean;
  readonly destructive?: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (text: string) => void;
}

export function ReasonDialog({
  title,
  description,
  fieldLabel,
  fieldPlaceholder,
  confirmLabel,
  requireText,
  busy = false,
  destructive = false,
  onCancel,
  onConfirm,
}: ReasonDialogProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const canConfirm = !busy && (requireText ? trimmed.length > 0 : true);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label
            htmlFor={id}
            className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
          >
            {fieldLabel}
            {requireText && <span className="text-destructive ml-0.5">*</span>}
          </label>
          <Textarea
            id={id}
            value={text}
            rows={4}
            placeholder={fieldPlaceholder}
            onChange={(event) => {
              setText(event.target.value);
            }}
            disabled={busy}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            {t('ui.dialog.cancel')}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => {
              if (!canConfirm) return;
              onConfirm(trimmed);
            }}
            disabled={!canConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
